// components/session/SessionManagement/RescheduleModal.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, isWeekend, addDays } from 'date-fns';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import {
    fetchAvailableSlots,
    rescheduleSession,
    selectAvailableSlots,
    selectSessionsLoading
} from '../../../store/slices/sessionSlice';
import { Button } from '../../../styles/components';
import {
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    TimeSlotGrid,
    TimeSlot,
    ModalFooter,
    LoadingSpinner
} from './ModalStyles';

const RescheduleModal = ({ session, onClose }) => {
    const dispatch = useDispatch();
    const availableSlots = useSelector(selectAvailableSlots);
    const loading = useSelector(selectSessionsLoading);

    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (selectedDate) {
            dispatch(fetchAvailableSlots(selectedDate));
        }
    }, [selectedDate, dispatch]);

    const handleDateSelect = (date) => {
        if (isWeekend(date) && date >= new Date()) {
            setSelectedDate(date);
            setSelectedTime(null); // Reset time selection when date changes
        }
    };

    const handleReschedule = async () => {
        if (!selectedDate || !selectedTime) {
            toast.error('Please select both date and time');
            return;
        }

        try {
            setSubmitting(true);
            await dispatch(rescheduleSession({
                sessionId: session.id,
                newDate: selectedDate,
                newTimeSlot: selectedTime
            })).unwrap();
            
            toast.success('Session rescheduled successfully');
            onClose();
        } catch (error) {
            toast.error(error.message || 'Failed to reschedule session');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={e => e.stopPropagation()}>
                <ModalHeader>
                    <h2>Reschedule Session</h2>
                    <button className="close-button" onClick={onClose}>
                        <X size={24} />
                    </button>
                </ModalHeader>

                <ModalBody>
                    <div className="section">
                        <h3>Current Session</h3>
                        <p className="current-session">
                            {format(session.date.toDate(), 'PPP')} at {session.timeSlot}
                        </p>
                    </div>

                    <div className="section">
                        <h3>Select New Date</h3>
                        <p className="hint">Only weekend slots available</p>
                        <div className="date-picker-container">
                            <DatePicker
                                selected={selectedDate}
                                onChange={handleDateSelect}
                                minDate={new Date()}
                                maxDate={addDays(new Date(), 30)}
                                filterDate={isWeekend}
                                inline
                            />
                        </div>
                    </div>

                    {selectedDate && (
                        <div className="section">
                            <h3>Select New Time</h3>
                            {loading ? (
                                <LoadingSpinner />
                            ) : availableSlots.length > 0 ? (
                                <TimeSlotGrid>
                                    {availableSlots.map(slot => (
                                        <TimeSlot
                                            key={slot}
                                            selected={selectedTime === slot}
                                            onClick={() => setSelectedTime(slot)}
                                            type="button"
                                        >
                                            {slot}
                                        </TimeSlot>
                                    ))}
                                </TimeSlotGrid>
                            ) : (
                                <p className="no-slots">No available slots for selected date</p>
                            )}
                        </div>
                    )}

                    {selectedDate && selectedTime && (
                        <div className="section summary">
                            <h3>New Session Time</h3>
                            <p className="new-session">
                                {format(selectedDate, 'PPP')} at {selectedTime}
                            </p>
                        </div>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button 
                        variant="outlined" 
                        onClick={onClose}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleReschedule}
                        disabled={!selectedDate || !selectedTime || submitting}
                    >
                        {submitting ? 'Rescheduling...' : 'Confirm Reschedule'}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </ModalOverlay>
    );
};

export default RescheduleModal;